import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import { qk } from "../keys";
import type { SensorNode } from "../models";

/**
 * 상시 띄워둔 관제 탭이 옛 스냅샷을 영원히 보여주지 않도록 30초마다 되받는다.
 * 전역 기본값은 refetchOnWindowFocus:false 라, 포커스를 주지 않는 벽걸이 화면은
 * 이 주기가 유일한 갱신 경로다. 신선도 판정(§2)의 stale 임계값이 150초이므로
 * 30초 주기면 지연을 놓치지 않는다.
 */
const LIVE_REFETCH_MS = 30_000;

interface SensorNodeList {
  next: string | null;
  previous: string | null;
  results: SensorNode[];
}

export function useSensorNodes(params?: {
  site?: number;
  status?: SensorNode["status"];
  manager?: number;
}) {
  return useQuery({
    queryKey: qk.iot.nodes.list(params),
    queryFn: () =>
      api.get<SensorNodeList>("/iot/nodes/", { params }).then((r) => r.data),
    refetchInterval: LIVE_REFETCH_MS,
  });
}

export function useSensorNode(id: number | undefined) {
  return useQuery({
    queryKey: id ? qk.iot.nodes.detail(id) : qk.iot.nodes.detail(-1),
    queryFn: () =>
      api.get<SensorNode>(`/iot/nodes/${id}/`).then((r) => r.data),
    enabled: !!id,
    refetchInterval: LIVE_REFETCH_MS,
  });
}

/** Response of `POST /iot/nodes/{id}/issue_key` — plaintext shown ONCE. */
export interface IssueNodeKeyResult {
  api_key: string;
  api_key_prefix: string;
  rotated_at: string;
}

/**
 * Issue (or rotate) a node's direct-ingest `egn_` key. The plaintext key is
 * returned exactly once — surface it to the operator immediately (copy to the
 * sensor's `[CLOUD] node_api_key`); it can never be read back. Re-issuing a
 * keyed node rotates it, invalidating the previous key. On success we refetch
 * the node list so the `has_api_key` / prefix badges update.
 */
export function useIssueNodeKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api
        .post<IssueNodeKeyResult>(`/iot/nodes/${id}/issue_key/`)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.iot.nodes.all }),
  });
}

/** Response of `POST /iot/nodes/{id}/pair_code` — a short-lived pairing code. */
export interface NodePairCodeResult {
  code: string;
  /** ISO-8601 expiry (5 minutes out); drives the countdown in the modal. */
  expires_at: string;
}

/**
 * Issue a short-lived (5 min) pairing code for a node. The operator reads the
 * code to a booting sensor, which trades it at `POST /iot/nodes/claim` for the
 * node's real `egn_` key — so the 44-char key never has to be hand-typed.
 * Re-issuing a node's code invalidates any previous one (server-side). No list
 * invalidation: the node's `egn_` key isn't minted until the sensor claims.
 */
export function useNodePairCode() {
  return useMutation({
    mutationFn: (id: number) =>
      api
        .post<NodePairCodeResult>(`/iot/nodes/${id}/pair_code/`)
        .then((r) => r.data),
  });
}
